import React, { useState, useRef, useEffect } from 'react';

interface TagEditorProps {
  tags: string[];
  onUpdate: (tags: string[]) => void;
  editable?: boolean;
  maxTags?: number;
}

const TagEditor: React.FC<TagEditorProps> = ({
  tags,
  onUpdate,
  editable = true,
  maxTags = 5
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [localTags, setLocalTags] = useState<string[]>(tags);
  const [inputValue, setInputValue] = useState('');
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [editingValue, setEditingValue] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const editInputRef = useRef<HTMLInputElement>(null);

  // Props'tan tags değiştiğinde local state'i güncelle
  useEffect(() => {
    setLocalTags(tags);
  }, [tags]);

  // Düzenleme moduna geçtiğinde input'a focus
  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isEditing]);

  // Tag düzenleme moduna geçtiğinde input'a focus
  useEffect(() => {
    if (editingIndex !== null && editInputRef.current) {
      editInputRef.current.focus();
    }
  }, [editingIndex]);

  const handleAddTag = () => {
    const newTag = inputValue.trim();
    if (newTag && !localTags.includes(newTag) && localTags.length < maxTags) {
      const newTags = [...localTags, newTag];
      setLocalTags(newTags);
      setInputValue('');
      onUpdate(newTags);
    }
  };

  const handleRemoveTag = (index: number) => {
    const newTags = localTags.filter((_, i) => i !== index);
    setLocalTags(newTags);
    onUpdate(newTags);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddTag();
    } else if (e.key === 'Escape') {
      setIsEditing(false);
      setInputValue('');
    } else if (e.key === 'Backspace' && inputValue === '' && localTags.length > 0) {
      // Boş input'ta backspace ile son tag'i sil
      handleRemoveTag(localTags.length - 1);
    }
  };

  const handleTagEdit = (index: number) => {
    setEditingIndex(index);
    setEditingValue(localTags[index]);
  };

  const handleTagEditSave = () => {
    if (editingIndex !== null && editingValue.trim()) {
      const newTags = [...localTags];
      newTags[editingIndex] = editingValue.trim();
      setLocalTags(newTags);
      onUpdate(newTags);
    }
    setEditingIndex(null);
    setEditingValue('');
  };

  const handleTagEditKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleTagEditSave();
    } else if (e.key === 'Escape') {
      setEditingIndex(null);
      setEditingValue('');
    }
  };

  const handleContainerClick = () => {
    if (editable && !isEditing) {
      setIsEditing(true);
    }
  };

  const handleBlur = (e: React.FocusEvent) => {
    // İlgili element container dışında mı kontrol et
    const relatedTarget = e.relatedTarget as HTMLElement;
    if (relatedTarget && e.currentTarget.contains(relatedTarget)) {
      return;
    }
    
    // Eğer input'ta değer varsa, tag olarak ekle
    if (inputValue.trim()) {
      handleAddTag();
    }
    
    setTimeout(() => {
      setIsEditing(false);
    }, 200);
  };

  return (
    <div 
      className={`tag-editor ${isEditing ? 'editing' : ''} ${editable ? 'editable' : ''}`}
      onClick={handleContainerClick}
      onBlur={handleBlur}
    >
      <div className="tag-list">
        {localTags.map((tag, index) => (
          <div 
            key={index} 
            className="tag-item"
            onDoubleClick={() => editable && handleTagEdit(index)}
          >
            {editingIndex === index ? (
              <input
                ref={editInputRef}
                type="text"
                value={editingValue}
                onChange={(e) => setEditingValue(e.target.value)}
                onKeyDown={handleTagEditKeyDown}
                onBlur={handleTagEditSave}
                className="tag-edit-input"
              />
            ) : (
              <>
                <span className="tag-text">{tag}</span>
                {editable && (
                  <button
                    className="tag-remove"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRemoveTag(index);
                    }}
                    title="Etiketi sil"
                  >
                    ×
                  </button>
                )}
              </>
            )}
          </div>
        ))}
        
        {isEditing && localTags.length < maxTags && (
          <input
            ref={inputRef}
            type="text"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Yeni etiket..."
            className="tag-input"
          />
        )}
      </div>
      
      {!isEditing && localTags.length === 0 && (
        <span className="tag-placeholder">
          {editable ? '+ Etiket ekle' : 'Etiket yok'}
        </span>
      )}
      
      {editable && !isEditing && localTags.length > 0 && localTags.length < maxTags && (
        <button 
          className="tag-add-btn"
          onClick={(e) => {
            e.stopPropagation();
            setIsEditing(true);
          }}
          title="Etiket ekle"
        >
          +
        </button>
      )}
    </div>
  );
};

export default TagEditor;


